import axios from 'axios';
import store from 'store/ReduxStoreContainer';
import { apiKeySelector } from 'store/appSettings/selectors';
import {
  DistanceMatrixResult,
  RouteResult,
  GeoCodeLocation,
  PlacePrediction,
  GeoCodeOptions
} from './types';
import { getLocationCallData } from './utils';
import { ENDPOINT_NAMES, IS_SERVICE_EXPANSION_ENABLED } from './constant';

/*
 * Method: getDistanceMatrix
 * Description: Returns a DistanceMatrix from an origin and destinations.
 * Accepts:
 *   origin: string (can be an address string or a lat lng string. example: '20.776,-56.2883')
 *   destinations: string[] (can be an address string or a lat lng string. example: '20.776,-56.2883')
 * Docs: https://developers.google.com/maps/documentation/distance-matrix/intro
 */
export async function getDistanceMatrix(
  origin: string,
  destinations: string[]
): Promise<DistanceMatrixResult> {
  const originEncoded = encodeURIComponent(origin);
  const destinationsEncoded = destinations
    .map(destination => encodeURIComponent(destination))
    .join('|');

  const callData = getLocationCallData({
    data: {
      destinations: destinationsEncoded,
      origins: originEncoded,
      units: 'imperial'
    },
    endpointName: ENDPOINT_NAMES.MATRIX_URL
  });

  const headers = {
    apiKey: apiKeySelector(store.getState()),
    ...callData.headers
  };

  return axios(callData.url, { headers }).then((res: { data: DistanceMatrixResult }) => res.data);
}

/*
 * Method: getDirections
 * Description: Returns a RouteResult from an origin and destination.
 * Accepts:
 *   origin: string (can be an address string or a lat lng string. example: '20.776,-56.2883')
 *   destination: string (can be an address string or a lat lng string. example: '20.776,-56.2883')
 * Docs: https://developers.google.com/maps/documentation/directions/start
 */
export async function getDirections(origin: string, destination: string): Promise<RouteResult> {
  const originEncoded = encodeURIComponent(origin);
  const destinationEncoded = encodeURIComponent(destination);

  const callData = getLocationCallData({
    data: {
      origin: originEncoded,
      destination: destinationEncoded
    },
    endpointName: ENDPOINT_NAMES.DIRECTIONS_URL
  });

  const headers = { apiKey: apiKeySelector(store.getState()), ...callData.headers };

  return axios(callData.url, { headers }).then((res: { data: RouteResult }) => res.data);
}

/*
 * Method: geoCodeLocation
 * Description: Returns a GeoCodeLocation[] from an address
 * Accepts:
 *   address: string (can be an address string or a lat lng string. example: '20.776,-56.2883')
 * Docs: https://developers.google.com/maps/documentation/geocoding/intro
 */
export async function geoCodeLocation(
  address: string,
  options?: GeoCodeOptions
): Promise<GeoCodeLocation[]> {
  const callData = getLocationCallData({
    data: {
      address
    },
    endpointName: ENDPOINT_NAMES.GEOCODE_URL,
    params: options
  });

  const headers = {
    apiKey: apiKeySelector(store.getState()),
    ...callData.headers
  };

  return axios(callData.url, { headers }).then((res: Object) => {
    return IS_SERVICE_EXPANSION_ENABLED ? res.data.data : res.data;
  });
}

/*
 * Method: getPlacePredictions
 * Description: Returns a GeoCodeLocation[] from an address
 * Accepts:
 *   address: string (can be an address string or a lat lng string. example: '20.776,-56.2883')
 * Docs: https://developers.google.com/maps/documentation/geocoding/intro
 */
export async function getPlacePredictions(
  address: string,
  useGeoLocation = false
): Promise<PlacePrediction[] | GeoCodeLocation[]> {
  if (useGeoLocation) return geoCodeLocation(address);

  const encodedAddress = encodeURIComponent(address);

  const callData = getLocationCallData({
    data: {
      address: encodedAddress
    },
    endpointName: ENDPOINT_NAMES.PLACE_PREDICTIONS_URL
  });

  const headers = {
    apiKey: apiKeySelector(store.getState()),
    ...(IS_SERVICE_EXPANSION_ENABLED ? { 'mhp-adapter': 'mhp' } : {}),
    ...callData.headers
  };

  return axios(callData.url, {
    headers
  }).then((res: { data: PlacePrediction[] | GeoCodeLocation[] }) =>
    IS_SERVICE_EXPANSION_ENABLED ? res.data.data : res.data
  );
}
